<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import Label from '@/components/ui/label/Label.vue'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { City, normalizeText, useCityFetch } from '@/composables/useCityFetch'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-vue-next'
import { ref, watch } from 'vue'

const props = withDefaults(
    defineProps<{
        modelValue: string
        hasForeignDoc: boolean
        state: string
        required?: boolean
        error?: string
        storedCity?: string
    }>(),
    {
        required: true,
        error: '',
    },
)

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
    (e: 'update:stored-city', value: string): void
}>()

const { cities, filteredCities, isLoading, searchQuery, selectedCity, fetchCities, selectCity, reset } = useCityFetch()
const cityOpen = ref(false)

function syncSelectedCityByName(cityName: string) {
    if (!cityName || !cities.value.length) return

    const matchedCity = cities.value.find((city) => normalizeText(city.name) === normalizeText(cityName))

    if (matchedCity) {
        selectCity(matchedCity)
        emit('update:modelValue', matchedCity.name)
    }
}

function handleSelectCity(city: City) {
    selectCity(city)
    emit('update:modelValue', city.name)
    cityOpen.value = false
}

watch(
    () => props.state,
    async (newState, oldState) => {
        if (props.hasForeignDoc) return

        const hasStateChanged = oldState !== undefined && newState !== oldState

        if (hasStateChanged) {
            selectedCity.value = null
            searchQuery.value = ''
            emit('update:modelValue', '')

            if (props.storedCity) {
                emit('update:modelValue', props.storedCity)
                emit('update:stored-city', '')
            }
        }

        if (!newState) {
            reset()
            emit('update:modelValue', '')
            return
        }

        await fetchCities(newState)
    },
    { immediate: true },
)

watch(
    () => props.modelValue,
    (newCity) => {
        if (props.hasForeignDoc) return

        if (!newCity) {
            selectedCity.value = null
            return
        }

        syncSelectedCityByName(newCity)
    },
)
</script>

<template>
    <div class="flex flex-col gap-1.5">
        <Label :required="props.required" for="city">{{ $t('City') }}</Label>

        <Popover v-model:open="cityOpen" v-if="!props.hasForeignDoc">
            <PopoverTrigger as-child>
                <Button
                    variant="outline"
                    role="combobox"
                    :aria-expanded="cityOpen"
                    :disabled="!props.state"
                    class="text-foreground hover:text-foreground focus-visible:border-light-blue focus-visible:ring-light-blue/50 flex h-12 w-full min-w-0 items-center justify-between rounded-lg border px-3 py-1 text-base font-normal shadow-none transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                    :class="{ 'text-muted-foreground': !selectedCity && !props.modelValue }"
                >
                    <span class="truncate">
                        {{ selectedCity?.name || props.modelValue || $t('Select') }}
                    </span>
                    <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent class="w-(--reka-popover-trigger-width) p-0" align="start">
                <Command class="w-full">
                    <CommandInput v-model="searchQuery" :placeholder="$t('Search city...')" />
                    <CommandList>
                        <div v-if="isLoading" class="text-muted-foreground flex items-center justify-center py-6 text-sm">
                            <Loader2 class="mr-2 h-4 w-4 animate-spin" />
                            {{ $t('Loading...') }}
                        </div>
                        <div v-else-if="cities.length === 0 && !isLoading" class="text-muted-foreground py-6 text-center text-sm">
                            {{ $t('Select a state first') }}
                        </div>
                        <CommandEmpty v-else-if="filteredCities.length === 0">
                            {{ $t('No city found.') }}
                        </CommandEmpty>
                        <CommandGroup v-else>
                            <CommandItem
                                v-for="city in filteredCities"
                                :key="city.id"
                                :value="`${city.name}-${city.id}`"
                                @select="() => handleSelectCity(city)"
                            >
                                <Check class="mr-2 h-4 w-4" :class="selectedCity?.id === city.id ? 'opacity-100' : 'opacity-0'" />
                                {{ city.name }}
                            </CommandItem>
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>

        <Input
            type="text"
            id="city"
            :model-value="props.modelValue"
            @update:model-value="(value) => emit('update:modelValue', value as string)"
            :placeholder="$t('City')"
            v-else
        />

        <p v-if="props.error" class="text-destructive text-xs">
            {{ props.error }}
        </p>
    </div>
</template>
